import { createBrowserRouter, Navigate } from 'react-router-dom';

import { accountingRoutes } from '@/features/accounting/routes';
import { authRoutes } from '@/features/auth/routes';
import { cashCodRoutes } from '@/features/cash-cod/routes';
import { catalogRoutes } from '@/features/catalog/routes';
import { customersRoutes } from '@/features/customers/routes';
import { deliveryRidersRoutes } from '@/features/delivery-riders/routes';
import { employeesRoutes } from '@/features/employees/routes';
import { homeRoutes } from '@/features/home/routes';
import { inventoryRoutes } from '@/features/inventory/routes';
import { offlineOrdersRoutes } from '@/features/offline-orders/routes';
import { ordersRoutes } from '@/features/orders/routes';
import { refundsRoutes } from '@/features/refunds';
import { reportsRoutes } from '@/features/reports/routes';
import { settingsRoutes } from '@/features/settings/routes';
import { storesRoutes } from '@/features/stores/routes';
import { adminsRoutes } from '@/features/admins/routes';
import { couponsRoutes } from '@/features/coupons';
import { deliveryChargesRoutes } from '@/features/delivery-charges';
import { promotionsRoutes } from '@/features/promotions';
import { supportRoutes } from '@/features/support/routes';

import { RootLayout } from './layouts/RootLayout';
import { RootErrorPage } from './pages/RootErrorPage';

/**
 * Single source of truth for the app's route tree.
 * RootLayout boots session; PublicLayout and AuthedLayout load as lazy chunks.
 * Feature route tables are imported from each feature routes module (not barrels).
 * RouterProvider is wrapped in Suspense in main.tsx for lazy route resolution.
 */
export const router = createBrowserRouter([
  {
    element: <RootLayout />,
    errorElement: <RootErrorPage />,
    children: [
      {
        lazy: async () => {
          const { PublicLayout } = await import('./layouts/PublicLayout');
          return { Component: PublicLayout };
        },
        children: authRoutes,
      },

      {
        lazy: async () => {
          const { AuthedLayout } = await import('./layouts/AuthedLayout');
          return { Component: AuthedLayout };
        },
        children: [
          { index: true, element: <Navigate to="/home" replace /> },

          ...homeRoutes,
          ...ordersRoutes,
          ...offlineOrdersRoutes,
          ...inventoryRoutes,
          ...refundsRoutes,
          ...catalogRoutes,
          ...storesRoutes,
          ...employeesRoutes,
          ...deliveryRidersRoutes,
          ...customersRoutes,
          ...adminsRoutes,
          ...couponsRoutes,
          ...deliveryChargesRoutes,
          ...promotionsRoutes,
          ...accountingRoutes,
          ...cashCodRoutes,
          ...reportsRoutes,
          ...supportRoutes,
          ...settingsRoutes,
        ],
      },

      {
        path: '403',
        lazy: async () => {
          const { ForbiddenPage } = await import('./pages/ForbiddenPage');
          return { Component: ForbiddenPage };
        },
      },
      {
        path: '*',
        lazy: async () => {
          const { NotFoundPage } = await import('./pages/NotFoundPage');
          return { Component: NotFoundPage };
        },
      },
    ],
  },
]);
